import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import {
  UIThread,
  ThreadMessageFeedbackRequestFeedback,
} from "../../generated/core-api";

export function useThreadMessageFeedback(
  workspaceId: string,
  threadId: string,
  messageIndex: number
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["workspace", workspaceId, "thread", threadId],
    [workspaceId, threadId],
  );

  const setFeedback = useCallback(
    async (feedback: ThreadMessageFeedbackRequestFeedback) => {
      setIsSubmitting(true);
      try {
        await api.setThreadMessageFeedback(
          workspaceId,
          threadId,
          messageIndex,
          {
            feedback,
          }
        );

        // Optimistically update the query cache
        queryClient.setQueryData<UIThread>(queryKey, (current) => {
          if (!current) return current;

          const updatedThread = { ...current };
          if (!updatedThread.feedback) {
            updatedThread.feedback = {};
          }

          const messageKey = messageIndex.toString();
          if (feedback === 0) {
            // Remove feedback
            delete updatedThread.feedback[messageKey];
          } else {
            // Set feedback
            updatedThread.feedback[messageKey] = feedback;
          }

          return updatedThread;
        });
      } catch (error) {
        console.error("Failed to set message feedback:", error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [workspaceId, threadId, messageIndex, queryClient, queryKey]
  );

  return {
    setFeedback,
    isSubmitting,
  };
}
